/**
 * PATH: apps/quote/src/pages/templates-page/TemplateListRow.tsx
 * PURPOSE: List-view table row for a template
 */

import { FileSpreadsheet, Eye } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { SpreadsheetTemplate, TemplateStatus } from '@/lib/quoteApi'

const STATUS_COLORS: Record<TemplateStatus, { bg: string; text: string }> = {
  draft: { bg: 'bg-amber-100 dark:bg-amber-900/30', text: 'text-amber-700 dark:text-amber-400' },
  active: { bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-700 dark:text-green-400' },
  archived: { bg: 'bg-gray-100 dark:bg-gray-700', text: 'text-gray-600 dark:text-gray-400' },
}

interface TemplateListRowProps {
  template: SpreadsheetTemplate
  onUseTemplate: (template: SpreadsheetTemplate) => void
  onEdit: (template: SpreadsheetTemplate) => void
}

export default function TemplateListRow({ template, onUseTemplate, onEdit }: TemplateListRowProps) {
  return (
    <tr className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50">
      <td className="px-4 py-3">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
            <FileSpreadsheet className="h-4 w-4 text-white" />
          </div>
          <div>
            <div className="font-medium text-slate-900 dark:text-white">{template.name}</div>
            <div className="text-xs text-slate-500 dark:text-slate-400">{template.description || 'No description'}</div>
          </div>
        </div>
      </td>
      <td className="px-4 py-3"><span className="capitalize text-sm text-slate-600 dark:text-slate-400">{template.category}</span></td>
      <td className="px-4 py-3">
        <span className={cn('inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full font-medium', STATUS_COLORS[template.status].bg, STATUS_COLORS[template.status].text)}>{template.status}</span>
      </td>
      <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-400">{template.binding_count}</td>
      <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-400">{template.times_used}x</td>
      <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-400">v{template.version}</td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-1">
          <button onClick={() => onEdit(template)} className="p-1.5 hover:bg-slate-100 dark:hover:bg-slate-700 rounded"><Eye className="h-4 w-4 text-slate-500" /></button>
          <button onClick={() => onUseTemplate(template)} disabled={template.status !== 'active'} className={cn('px-3 py-1 text-sm font-medium rounded transition-colors', template.status === 'active' ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-slate-100 text-slate-400 cursor-not-allowed')}>Use</button>
        </div>
      </td>
    </tr>
  )
}
